import type {
  WorkRequestDraft,
  WorkRequestIntakeState,
} from "../types/workRequest";

export function isDraftIncomplete(draft: WorkRequestDraft) {
  const title = draft.workRequestTitle?.trim();
  return (
    !draft.assetId ||
    !title ||
    draft.titleIsFallback === true ||
    !draft.workRequestDescription?.trim()
  );
}

export type IntakeNextStep =
  /** The model was not used; the requester reviews the heuristic draft in the form. */
  | "form-unavailable"
  /** The asset is resolved but the draft is too thin to review; finish it in the form. */
  | "form-incomplete"
  /** The asset is resolved and the draft is complete; run the pre-submit assessment. */
  | "assess"
  /** Several assets match; ask the requester which one. */
  | "pick-asset"
  /** No asset yet and nothing to choose from; keep chatting. */
  | "continue-chat";

/** Decides where a requester goes after an intake reply. */
export function nextStepAfterIntake(
  state: WorkRequestIntakeState,
): IntakeNextStep {
  // Without the model the draft is heuristic, so the requester reviews it in
  // the form rather than continuing a chat the model is not part of.
  if (state.aiAssistance === "UNAVAILABLE") return "form-unavailable";
  if (state.assetCandidates.length > 0) return "pick-asset";
  if (state.draft.assetId && state.draft.assetName) {
    return isDraftIncomplete(state.draft) ? "form-incomplete" : "assess";
  }
  return "continue-chat";
}

/**
 * Applies a message the requester sent when the model could not be reached.
 * The message becomes the description when there is none yet; otherwise it
 * returns to the composer and the captured draft is left as is.
 */
export function recoverUnavailableSend(
  state: WorkRequestIntakeState,
  userMessage: string,
): { state: WorkRequestIntakeState; restoreMessage: boolean } {
  if (state.draft.workRequestDescription?.trim()) {
    return { state, restoreMessage: true };
  }
  return {
    state: {
      ...state,
      draft: { ...state.draft, workRequestDescription: userMessage },
    },
    restoreMessage: false,
  };
}
